import { useEffect } from "react";
import { Link } from "react-router-dom";
import { useCourseManifest } from "@/lib/course";
import { useProgress, resumeLocation, lessonId } from "@/lib/progress";

// Wide, full-page course card — the design used on the Home dashboard and the
// /courses catalog page. (The slide-out drawer uses the compact
// CoursesCatalogList instead; its narrow chrome can't fit the summary + CTA.)

interface CourseCardForSlugProps {
  courseSlug: string;
  // Optional visibility predicate keyed on the resolved status. When provided,
  // the card renders only if showWhen(status) is true, and stays hidden until
  // manifest + progress resolve. Home and the full catalog omit it (show all);
  // "My Training" passes (s) => s !== "not-started"; catalog status filters
  // pass (s) => s === <selected>.
  showWhen?: (status: CourseStatus) => boolean;
  // Reports the resolved status (once both manifest + progress have loaded) so
  // a parent can drive empty states / counts.
  onStatusResolved?: (slug: string, status: CourseStatus) => void;
}

export function CourseCardForSlug({
  courseSlug,
  showWhen,
  onStatusResolved
}: CourseCardForSlugProps): JSX.Element | null {
  const manifest = useCourseManifest(courseSlug);
  const progress = useProgress(manifest.status === "ready" ? manifest.manifest.slug : "");

  const ready = manifest.status === "ready" && progress.status === "ready";
  const totalLessons =
    manifest.status === "ready"
      ? manifest.manifest.chapters.reduce((a, c) => a + c.lessons.length, 0)
      : 0;
  const status: CourseStatus = ready ? courseStatus(progress, totalLessons) : "not-started";

  useEffect(() => {
    if (ready) onStatusResolved?.(courseSlug, status);
  }, [ready, status, courseSlug, onStatusResolved]);

  if (manifest.status === "loading") {
    return showWhen ? null : (
      <div
        role="status"
        aria-live="polite"
        className="rounded-card border border-border bg-muted/30 h-24 motion-safe:animate-pulse"
      >
        <span className="sr-only">Loading course</span>
      </div>
    );
  }
  if (manifest.status === "error") {
    return (
      <div className="text-destructive text-sm">
        Failed to load {courseSlug}: {manifest.message}
      </div>
    );
  }
  // manifest ready; while filtering, hide until progress resolves and only show
  // courses the predicate accepts.
  if (showWhen && (!ready || !showWhen(status))) {
    return null;
  }
  return <CourseCard manifest={manifest.manifest} progress={progress} />;
}

interface CourseManifestShape {
  slug: string;
  title: string;
  summary: string;
  estimatedHours: number;
  chapters: Array<{ folder: string; title: string; lessons: Array<{ slug: string; title: string }> }>;
}

interface CourseCardProps {
  manifest: CourseManifestShape;
  progress: ReturnType<typeof useProgress>;
}

export type CourseStatus = "not-started" | "in-progress" | "completed";

function courseStatus(
  progress: ReturnType<typeof useProgress>,
  totalLessons: number
): CourseStatus {
  if (progress.status !== "ready") return "not-started";
  const completed = progress.progress.completedLessons.length;
  const visited = progress.progress.lastVisited !== null;
  if (totalLessons > 0 && completed >= totalLessons) return "completed";
  if (completed === 0 && !visited) return "not-started";
  return "in-progress";
}

function StatusPill({ status }: { status: CourseStatus }): JSX.Element {
  const cfg = {
    "not-started": { label: "Not started", classes: "bg-muted text-muted-foreground" },
    "in-progress": { label: "In progress", classes: "bg-primary/15 text-primary-soft" },
    "completed": { label: "Completed", classes: "bg-success/20 text-success" }
  }[status];
  return (
    <span
      className={`inline-block whitespace-nowrap rounded-full px-3 py-1 text-[11px] font-semibold tracking-wide ${cfg.classes}`}
    >
      {cfg.label}
    </span>
  );
}

function CourseCard({ manifest, progress }: CourseCardProps): JSX.Element {
  // First lesson in the flat sequence that isn't completed. Used as the
  // resume target when lastVisited is already complete.
  const nextIncompleteLoc = (() => {
    if (progress.status !== "ready") return null;
    const completed = new Set(progress.progress.completedLessons);
    for (const ch of manifest.chapters) {
      for (const ls of ch.lessons) {
        if (!completed.has(lessonId(ch.folder, ls.slug))) {
          return { chapter: ch.folder, lesson: ls.slug };
        }
      }
    }
    return null;
  })();
  const resume =
    progress.status === "ready" ? resumeLocation(progress.progress, nextIncompleteLoc) : null;
  const totalLessons = manifest.chapters.reduce((a, c) => a + c.lessons.length, 0);
  const status = courseStatus(progress, totalLessons);

  // Resume lesson title (for in-progress state)
  const resumeCh = resume ? manifest.chapters.find((c) => c.folder === resume.chapter) : undefined;
  const resumeLessonTitle = resume
    ? (resumeCh?.lessons.find((l) => l.slug === resume.lesson)?.title ?? "where you left off")
    : "";

  // First lesson in the course (for not-started state)
  const firstCh = manifest.chapters[0];
  const firstLesson = firstCh?.lessons[0];

  // State-driven card destination + CTA
  let cardHref: string;
  let ctaText: string;
  if (status === "not-started" && firstCh && firstLesson) {
    cardHref = `/courses/${manifest.slug}/${firstCh.folder}/${firstLesson.slug}`;
    ctaText = "Start course →";
  } else if (status === "completed") {
    cardHref = `/courses/${manifest.slug}/complete`;
    ctaText = "Review course →";
  } else if (resume) {
    cardHref = `/courses/${manifest.slug}/${resume.chapter}/${resume.lesson}`;
    ctaText = `Continue: ${resumeLessonTitle} →`;
  } else {
    cardHref = `/courses/${manifest.slug}`;
    ctaText = "Browse course →";
  }

  return (
    <Link
      to={cardHref}
      className="group block rounded-card border border-border bg-card px-5 py-4 shadow-card hover:border-primary/40 hover:shadow-card-hover hover:-translate-y-0.5 motion-reduce:hover:translate-y-0 transition-all"
    >
      <div className="flex items-center justify-between gap-4">
        <div className="min-w-0">
          <h2 className="text-base font-semibold truncate">{manifest.title}</h2>
          <p className="text-muted-foreground text-sm mt-0.5 truncate">{manifest.summary}</p>
          <p className="text-muted-foreground text-xs mt-0.5">
            {manifest.chapters.length} chapters · {totalLessons} lessons · ~{manifest.estimatedHours}h
          </p>
        </div>
        <div className="flex shrink-0 items-center gap-3">
          <StatusPill status={status} />
          <span className="text-sm font-semibold text-primary-soft group-hover:text-primary transition-colors whitespace-nowrap">
            {ctaText}
          </span>
        </div>
      </div>
    </Link>
  );
}
